import React from 'react';
import { useReactTable, getCoreRowModel, flexRender, createColumnHelper, ColumnDef } from '@tanstack/react-table';
import { Top10DiputadoStat } from '../utils/api';
import { useNavigate } from 'react-router-dom';
interface DiputadosTableProps {
  data: Top10DiputadoStat[];
  title: string;
  unit: string;
  valueColumnName: string;
  totalValue: number;
  averageValue?: number;
  averageLabel?: string;
  startDate: string;
  endDate: string;
}
function formatDate(dateString: string): string {
  const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const [year, month, day] = dateString.split('-');
  return `${parseInt(day)} ${months[parseInt(month) - 1]} ${year}`;
}
export function DiputadosTable({
  data,
  title,
  unit,
  valueColumnName,
  totalValue,
  averageValue,
  averageLabel = 'Promedio',
  startDate,
  endDate
}: DiputadosTableProps) {
  const navigate = useNavigate();
  const columnHelper = createColumnHelper<Top10DiputadoStat>();
  const columns: ColumnDef<Top10DiputadoStat, any>[] = [columnHelper.display({
    id: 'rank',
    header: '#',
    size: 40,
    cell: info => <span className={`font-display font-bold ${info.row.index === 0 ? 'text-xl text-purple' : 'text-base text-[#1A1A1A]'}`} data-id="element-51">
          {info.row.index + 1}
        </span>
  }), columnHelper.accessor(row => row.diputado.nombre, {
    id: 'nombre',
    header: 'Nombre',
    size: 300,
    cell: info => {
      const row = info.row.original;
      const fullName = info.getValue();
      const nameParts = fullName.split(' ');
      // If second part is shorter than 3 chars and there's a third part, show 3 parts
      let shortName: string;
      if (nameParts.length >= 3 && nameParts[1].length < 3) {
        shortName = nameParts.slice(0, 3).join(' ');
      } else {
        shortName = nameParts.slice(0, 2).join(' ');
      }
      return <div className="flex items-center gap-2" data-id="element-52">
            <span className={`text-sm md:text-base ${info.row.index === 0 ? 'font-bold' : ''}`} data-id="element-53">
              <span className="md:hidden" data-id="element-54">{shortName}</span>
              <span className="hidden md:inline" data-id="element-55">{fullName}</span>
            </span>
            {row.desafuero && <span className="text-base md:text-lg relative group" title="Desaforado por la justicia" data-id="element-56">
                🚨
                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1 bg-purple text-white text-xs font-display uppercase tracking-wide rounded-none border-2 border-yellow whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10" data-id="element-57">
                  Desaforado por la justicia
                </span>
              </span>}
            {row.guagua && <span className="text-base md:text-lg relative group" title="Prenatal/Postnatal" data-id="element-58">
                👶
                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1 bg-purple text-white text-xs font-display uppercase tracking-wide rounded-none border-2 border-yellow whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10" data-id="element-59">
                  Prenatal/Postnatal
                </span>
              </span>}
          </div>;
    }
  }), columnHelper.accessor(row => row.diputado.partido, {
    id: 'partido',
    header: 'Partido',
    size: 180,
    cell: info => <span className={`font-display uppercase text-sm ${info.row.index === 0 ? 'font-bold' : ''}`} data-id="element-60">
          {info.getValue()}
        </span>
  }), columnHelper.accessor(row => row.diputado.bancada, {
    id: 'bancada',
    header: 'Bancada',
    size: 240,
    cell: info => {
      const bancadas = info.getValue();
      const firstBancada = bancadas[0];
      const remaining = bancadas.length - 1;
      return <span className={`font-display uppercase text-sm ${info.row.index === 0 ? 'font-bold' : ''}`} data-id="element-61">
            {firstBancada}
            {remaining > 0 && <span className="italic normal-case" data-id="element-62">
                {' '}
                y otras {remaining} más
              </span>}
          </span>;
    }
  }), columnHelper.accessor('stat_value', {
    header: valueColumnName,
    size: 140,
    cell: info => <span className={`font-display font-bold text-purple ${info.row.index === 0 ? 'text-xl' : 'text-lg'}`} data-id="element-63">
          {unit === 'horas' ? info.getValue().toFixed(2) : info.getValue()}{' '}
          <span className="text-base text-[#1A1A1A]" data-id="element-64">{unit}</span>
        </span>
  }), columnHelper.accessor('stat_porcentaje', {
    header: 'Porcentaje',
    size: 240,
    cell: info => {
      const percentage = info.getValue();
      const formattedTotal = unit === 'horas' ? totalValue.toFixed(2) : totalValue.toLocaleString();
      return <div className="flex items-center justify-center gap-1 md:gap-2 whitespace-nowrap" data-id="element-65">
            <span className={`font-display font-bold text-[#1A1A1A] ${info.row.index === 0 ? 'text-lg md:text-xl' : 'text-base md:text-lg'}`} data-id="element-66">
              {percentage.toFixed(1)}%
            </span>
            <span className="text-xs text-[#1A1A1A]" data-id="element-67">de {formattedTotal}</span>
          </div>;
    }
  })];
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel()
  });
  return <div className="w-full" data-id="element-68">
      {/* Title - Hidden on mobile since accordion shows it */}
      <h3 className="hidden md:block font-display text-2xl font-bold text-purple uppercase tracking-tight mb-4" data-id="element-69">
        {title}
      </h3>

      {/* Header Card with Metadata */}
      <div className="bg-yellow border-brutal border-[#1A1A1A] rounded-none shadow-brutal p-4 mb-4" data-id="element-70">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-71">
          <div data-id="element-72">
            <div className="font-display text-xs text-[#1A1A1A] uppercase tracking-wider mb-1" data-id="element-73">
              Período
            </div>
            <div className="font-display text-base font-bold text-[#1A1A1A] leading-tight" data-id="element-74">
              {formatDate(startDate)}
              <span className="text-purple mx-2" data-id="element-75">→</span>
              {formatDate(endDate)}
            </div>
          </div>
          <div className="md:text-center" data-id="element-76">
            <div className="font-display text-xs text-[#1A1A1A] uppercase tracking-wider mb-1" data-id="element-77">
              Total {unit}
            </div>
            <div className="font-display text-2xl font-bold text-purple leading-tight" data-id="element-78">
              {unit === 'horas' ? totalValue.toFixed(2) : totalValue.toLocaleString()}
            </div>
          </div>
          {averageValue !== undefined && <div className="md:text-right" data-id="element-79">
              <div className="font-display text-xs text-[#1A1A1A] uppercase tracking-wider mb-1" data-id="element-80">
                {averageLabel}
              </div>
              <div className="font-display text-2xl font-bold text-purple leading-tight" data-id="element-81">
                {averageValue.toFixed(2)}
                <span className="text-base text-[#1A1A1A] ml-2" data-id="element-82">{unit}</span>
              </div>
            </div>}
        </div>
      </div>

      <div className="bg-white border-brutal border-[#1A1A1A] rounded-none shadow-brutal overflow-hidden" data-id="element-83">
        <table className="w-full table-fixed" data-id="element-84">
          <colgroup data-id="element-85">
            <col className="w-10 md:w-[40px]" data-id="element-86" />
            <col className="w-auto md:w-[300px]" data-id="element-87" />
            <col className="hidden md:table-column md:w-[180px]" data-id="element-88" />
            <col className="hidden md:table-column md:w-[240px]" data-id="element-89" />
            <col className="hidden md:table-column md:w-[140px]" data-id="element-90" />
            <col className="w-auto md:w-[240px]" data-id="element-91" />
          </colgroup>
          <thead className="bg-purple" data-id="element-92">
            {table.getHeaderGroups().map(headerGroup => <tr key={headerGroup.id} data-id="element-93">
                {headerGroup.headers.map(header => <th key={header.id} className={`font-display px-2 md:px-4 py-2 text-white uppercase tracking-wide text-xs border-b-brutal border-[#1A1A1A] ${header.id === 'stat_porcentaje' ? 'text-center' : 'text-left'} ${header.id === 'partido' || header.id === 'bancada' || header.id === 'stat_value' ? 'hidden md:table-cell' : ''}`} data-id="element-94">
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>)}
              </tr>)}
          </thead>
          <tbody data-id="element-95">
            {table.getRowModel().rows.map((row, index) => <tr key={row.id} onClick={() => navigate(`/diputado?id=${row.original.diputado.id}`)} className={`border-b-2 border-[#1A1A1A] transition-all duration-100 cursor-pointer ${index === 0 ? 'bg-yellow shadow-brutal hover:shadow-brutal-hover' : index % 2 === 0 ? 'bg-white hover:bg-yellow' : 'bg-[#F6F6F6] hover:bg-yellow'}`} data-id="element-96">
                {row.getVisibleCells().map(cell => <td key={cell.id} className={`px-2 md:px-4 py-3 text-[#1A1A1A] ${cell.column.id === 'partido' || cell.column.id === 'bancada' || cell.column.id === 'stat_value' ? 'hidden md:table-cell' : ''}`} data-id="element-97">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>)}
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}